import { useEffect, useRef } from "react";
import { useTheme } from "../context/ThemeContext";

const PARTICLE_COUNT = 110;
const CONNECTION_DISTANCE = 50;
const MAX_SPEED = 0.1;

function randomBetween(min, max) {
  return Math.random() * (max - min) + min;
}

export default function ParticleNetwork() {
  const canvasRef = useRef(null);
  const { theme } = useTheme();

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    const isDark = theme === "dark";

    const colors = {
      particle: isDark ? "#00FF41" : "#00A82D",
      line: isDark ? "#00FFFF" : "#00A82D",
      globalAlpha: isDark ? 0.40 : 0.50,
    };

    let width = window.innerWidth;
    let height = window.innerHeight;
    canvas.width = width;
    canvas.height = height;

    // Initialize particles
    const particles = Array.from({ length: PARTICLE_COUNT }, () => ({
      x: Math.random() * width,
      y: Math.random() * height,
      vx: randomBetween(-0.8, 0.8) || 0.3,
      vy: randomBetween(-0.8, 0.8) || 0.3,
      radius: randomBetween(1.5, 3),
    }));

    // Ensure minimum speed
    particles.forEach((p) => {
      if (Math.abs(p.vx) < 0.3) p.vx = p.vx < 0 ? -0.3 : 0.3;
      if (Math.abs(p.vy) < 0.3) p.vy = p.vy < 0 ? -0.3 : 0.3;
    });

    // Debounced resize
    let resizeTimer;
    const onResize = () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => {
        width = window.innerWidth;
        height = window.innerHeight;
        canvas.width = width;
        canvas.height = height;
        // Redistribute any out-of-bounds particles
        particles.forEach((p) => {
          p.x = Math.min(p.x, width);
          p.y = Math.min(p.y, height);
        });
      }, 100);
    };

    window.addEventListener("resize", onResize);

    let animId;
    let paused = false;
    let lastTime = performance.now();

    const onVisibilityChange = () => {
      paused = document.hidden;
      if (!paused) {
        lastTime = performance.now(); // Reset time when resuming
        tick(performance.now());
      }
    };
    document.addEventListener("visibilitychange", onVisibilityChange);

    const tick = (currentTime) => {
      if (paused) return;

      const deltaSeconds = (currentTime - lastTime) / 1000;
      lastTime = currentTime;

      // Cap deltaTime to avoid large jumps (e.g., after tab was hidden for a long time)
      const cappedDelta = Math.min(deltaSeconds, 0.1); // Max 100ms per frame
      const scaleFactor = cappedDelta * 60; // Convert velocity from per-frame to per-second

      animId = requestAnimationFrame(tick);

      ctx.clearRect(0, 0, width, height);
      ctx.globalAlpha = colors.globalAlpha;

      // Update and draw particles
      for (let i = 0; i < PARTICLE_COUNT; i++) {
        const p = particles[i];

        // Clamp speed
        const speed = Math.sqrt(p.vx * p.vx + p.vy * p.vy);
        if (speed > MAX_SPEED) {
          p.vx = (p.vx / speed) * MAX_SPEED;
          p.vy = (p.vy / speed) * MAX_SPEED;
        }

        // Move based on elapsed time
        p.x += p.vx * scaleFactor;
        p.y += p.vy * scaleFactor;

        // Bounce at edges
        if (p.x < 0) { p.x = 0; p.vx = Math.abs(p.vx); }
        if (p.x > width) { p.x = width; p.vx = -Math.abs(p.vx); }
        if (p.y < 0) { p.y = 0; p.vy = Math.abs(p.vy); }
        if (p.y > height) { p.y = height; p.vy = -Math.abs(p.vy); }

        // Draw particle
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.radius, 0, Math.PI * 2);
        ctx.fillStyle = colors.particle;
        ctx.fill();
      }

      // Draw connection lines (j > i to avoid duplicates)
      ctx.lineWidth = 0.5;
      for (let i = 0; i < PARTICLE_COUNT; i++) {
        for (let j = i + 1; j < PARTICLE_COUNT; j++) {
          const dx = particles[j].x - particles[i].x;
          const dy = particles[j].y - particles[i].y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist < CONNECTION_DISTANCE) {
            const opacity = 1 - dist / CONNECTION_DISTANCE;
            ctx.strokeStyle = colors.line;
            ctx.globalAlpha = colors.globalAlpha * opacity;
            ctx.beginPath();
            ctx.moveTo(particles[i].x, particles[i].y);
            ctx.lineTo(particles[j].x, particles[j].y);
            ctx.stroke();
            ctx.globalAlpha = colors.globalAlpha;
          }
        }
      }
    };

    tick(performance.now());

    return () => {
      cancelAnimationFrame(animId);
      clearTimeout(resizeTimer);
      window.removeEventListener("resize", onResize);
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
  }, [theme]);

  return (
    <canvas
      ref={canvasRef}
      aria-hidden="true"
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        zIndex: -1,
        pointerEvents: "none",
        display: "block",
      }}
    />
  );
}
